import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useTerritoryStore } from '../../../store/territoryStore';
import type { NamedAccount, NamedAccountSource } from '../../../types';

const PAGE_SIZE = 20;

function SourceBadge({ source }: { source: NamedAccountSource }) {
  const cls =
    source === 'BLAST_SYNC'
      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {source === 'BLAST_SYNC' ? 'Blast Sync' : 'Manual'}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface NamedAccountsTableProps {
  onAdd: () => void;
}

export default function NamedAccountsTable({ onAdd }: NamedAccountsTableProps) {
  const {
    namedAccounts,
    isLoadingNamedAccounts,
    namedAccountsError,
    namedAccountFilters,
    fetchNamedAccounts,
    setNamedAccountFilters,
    deleteNamedAccount,
  } = useTerritoryStore();

  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchNamedAccounts();
  }, [fetchNamedAccounts]);

  const handleSourceFilter = (source: string) => {
    setPage(1);
    setNamedAccountFilters({ source: source ? (source as NamedAccountSource) : undefined });
  };

  const handleDelete = async (account: NamedAccount) => {
    if (!confirm(`Remove named account for "${account.organization?.name ?? account.organizationId}"?`)) return;
    setDeletingId(account.id);
    try {
      await deleteNamedAccount(account.id);
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(namedAccounts.length / PAGE_SIZE));
  const paginated = namedAccounts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400">
            Source:
            <select
              value={namedAccountFilters.source ?? ''}
              onChange={(e) => handleSourceFilter(e.target.value)}
              className="ml-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="BLAST_SYNC">Blast Sync</option>
              <option value="MANUAL">Manual</option>
            </select>
          </label>
        </div>
        <button
          onClick={onAdd}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
        >
          + Add Named Account
        </button>
      </div>

      {/* Error */}
      {namedAccountsError && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {namedAccountsError}
        </p>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {isLoadingNamedAccounts ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading named accounts...</div>
        ) : namedAccounts.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No named accounts found. Add one to get started.
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Organization
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Assigned Rep
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Assigned Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {paginated.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      <span className="font-medium">
                        {account.organization?.name ?? account.organizationId}
                      </span>
                      {account.organization?.state && (
                        <span className="ml-1.5 text-gray-400 dark:text-gray-500 text-xs">
                          ({account.organization.state})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {account.assignedUser?.displayName ?? account.assignedUserId}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <SourceBadge source={account.source} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(account.assignedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(account)}
                        disabled={deletingId === account.id}
                        className="text-red-500 hover:text-red-700 disabled:opacity-40"
                        title="Remove named account"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, namedAccounts.length)} of {namedAccounts.length}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                    className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-xs disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === totalPages}
                    className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-xs disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
